import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  TableProps,
  Select,
  Button,
  PaginationProps,
  Modal,
  Space,
  Badge,
  Tooltip,
  Input,
  Tag,
  Checkbox,
  Alert,
  Typography,
} from 'antd';
import { getRequest, ResponseDataType, postRequest } from 'api';
import { toast } from 'react-toastify';
import { BillInfo } from 'Pages/Bill/Information';
import {
  MailOutlined,
  SearchOutlined,
  CalendarOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
} from '@ant-design/icons';
import { EllipsisTooltip } from './EllipsisTooltip';
import { ClassLevelSelect } from 'Components';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import {
  StudentBillGroup,
  groupBillsByStudent,
  getGroupSelectionState,
  toggleGroupSelectionKeys,
} from './studentBillGrouping';
import './UnpaidBillList.css';

const { Option } = Select;

interface unPaidBillDataType {
  invoidId: string;
  billId?: string;
  studentInvoiceId: number;
  studentId: number;
  studentName: string;
  className: string;
  createdDate: number;
  parentPhonenum: string;
  cost: number;
  content: string;
  description: string;
  status: number;
  paymentDate: string | null;
  updatedDate: number;
  schoolYear: string;
  month: string;
  year: string;
  studentCode: string;
  classYearName: string;
  notificationCount?: number;
}

interface IncomeSchoolOption {
  id: number;
  name: string;
  code?: string;
  isEinvoice?: boolean;
}

interface NotificationDataType {
  status: number;
}

const formatCurrency = (value: number): string => {
  const formattedValue = new Intl.NumberFormat('vi-VN').format(value || 0);
  return `${formattedValue} VNĐ`;
};

const MONTHS = [
  { label: 'Tháng 1', value: 1 },
  { label: 'Tháng 2', value: 2 },
  { label: 'Tháng 3', value: 3 },
  { label: 'Tháng 4', value: 4 },
  { label: 'Tháng 5', value: 5 },
  { label: 'Tháng 6', value: 6 },
  { label: 'Tháng 7', value: 7 },
  { label: 'Tháng 8', value: 8 },
  { label: 'Tháng 9', value: 9 },
  { label: 'Tháng 10', value: 10 },
  { label: 'Tháng 11', value: 11 },
  { label: 'Tháng 12', value: 12 },
];

export const UnpaidBillList = () => {
  const [unPaidBillList, setUnPaidBillList] = useState<unPaidBillDataType[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(() => getStoredWorkMonth().year);
  const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
  const [selectedClass, setSelectedClass] = useState<number | undefined>();
  const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);
  const [classList, setClassList] = useState<any[]>([]);
  const [incomeSchoolList, setIncomeSchoolList] = useState<IncomeSchoolOption[]>([]);
  const [selectedIncomeSchoolId, setSelectedIncomeSchoolId] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedStudentInvoiceId, setSelectedStudentInvoiceId] = useState<number | undefined>();
  const [hasChanged, setHasChanged] = useState(false);
  const [searchStudentName, setSearchStudentName] = useState<string>('');
  const [debouncedStudentName, setDebouncedStudentName] = useState<string>('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const schoolId = localStorage.getItem('schoolId');

  const years = useMemo(() => {
    const startYear = 2021;
    const endYear = Math.max(new Date().getFullYear() + 2, selectedYear + 1);
    const list: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      list.push(y);
    }
    return list;
  }, [selectedYear]);

  const studentGroups = useMemo(
    () => groupBillsByStudent(unPaidBillList),
    [unPaidBillList]
  );

  const multiBillGroupKeys = useMemo(
    () => studentGroups.filter((g) => g.billCount > 1).map((g) => g.groupKey),
    [studentGroups]
  );

  useEffect(() => {
    setExpandedRowKeys((prev) =>
      prev.filter((key) => multiBillGroupKeys.includes(String(key)))
    );
  }, [multiBillGroupKeys]);

  const isAllExpanded =
    multiBillGroupKeys.length > 0 &&
    multiBillGroupKeys.every((key) => expandedRowKeys.includes(key));

  const handleToggleExpandAll = () => {
    if (isAllExpanded) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys(multiBillGroupKeys);
    }
  };

  const allPageInvoiceIds = useMemo(
    () => unPaidBillList.map((item) => item.studentInvoiceId),
    [unPaidBillList]
  );

  const isPageAllChecked =
    allPageInvoiceIds.length > 0 &&
    allPageInvoiceIds.every((id) => selectedRowKeys.includes(id));

  const isPageIndeterminate =
    !isPageAllChecked &&
    allPageInvoiceIds.some((id) => selectedRowKeys.includes(id));

  const handleToggleSelectAllPage = () => {
    if (isPageAllChecked) {
      const pageSet = new Set<React.Key>(allPageInvoiceIds);
      setSelectedRowKeys((prev) => prev.filter((k) => !pageSet.has(k)));
    } else {
      const nextSet = new Set<React.Key>(selectedRowKeys);
      for (const id of allPageInvoiceIds) {
        nextSet.add(id);
      }
      setSelectedRowKeys(Array.from(nextSet));
    }
  };

  const selectedBills = useMemo(
    () => unPaidBillList.filter((item) => selectedRowKeys.includes(item.studentInvoiceId)),
    [unPaidBillList, selectedRowKeys]
  );

  const totalSelectedAmount = useMemo(
    () => selectedBills.reduce((sum, item) => sum + (item.cost || 0), 0),
    [selectedBills]
  );

  const selectedStudentCount = useMemo(
    () => new Set(selectedBills.map((item) => item.studentId || -item.studentInvoiceId)).size,
    [selectedBills]
  );

  const fetchUnpaidBills = async () => {
    setLoading(true);
    try {
      const queryParams: Record<string, string> = {
        page: currentPage.toString(),
        perPage: pageSize.toString(),
        month: selectedMonth.toString(),
        year: selectedYear.toString(),
        schoolId: schoolId || '',
        status: '0',
        groupByStudent: 'true',
      };
      if (selectedClass) {
        queryParams['classId'] = selectedClass.toString();
      } else if (selectedClassLevel) {
        queryParams['classLevel'] = selectedClassLevel;
      }
      if (debouncedStudentName) {
        queryParams['studentName'] = debouncedStudentName;
      }
      if (selectedIncomeSchoolId !== undefined && selectedIncomeSchoolId !== null) {
        queryParams['incomeSchoolId'] = selectedIncomeSchoolId.toString();
      }
      const queryString = new URLSearchParams(queryParams).toString();

      const url = `/cms/invoice/list-invoices?${queryString}`;
      const response: ResponseDataType<{ list: unPaidBillDataType[]; total: number }> = await getRequest(url);

      if (response?.code === 200) {
        setUnPaidBillList(response.info?.list || []);
        setTotalItems(response.info?.total || 0);
      } else {
        setUnPaidBillList([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching unpaid bills:', error);
      setTotalItems(0);
      setUnPaidBillList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStudentName(searchStudentName.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchStudentName]);

  const handlePressEnter = () => {
    setDebouncedStudentName(searchStudentName.trim());
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleWorkMonthChange = (e: any) => {
      if (e.detail) {
        setSelectedMonth(e.detail.month);
        setSelectedYear(e.detail.year);
        setCurrentPage(1);
      }
    };
    window.addEventListener('workMonthChanged', handleWorkMonthChange);
    return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
  }, []);

  useEffect(() => {
    fetchUnpaidBills();
    setSelectedRowKeys([]);
  }, [selectedYear, selectedMonth, currentPage, pageSize, selectedClass, selectedClassLevel, debouncedStudentName, selectedIncomeSchoolId]);

  useEffect(() => {
    const fetchClasses = async () => {
      if (!schoolId) return;
      const url = `/cms/class/get-class-years-by-start-end-schoolId?schoolId=${schoolId}&start=${selectedYear}&page=1&perPage=1000`;
      const res: ResponseDataType<any> = await getRequest(url);
      if (res?.code === 200) {
        setClassList(res?.info?.list || []);
      }
    };
    fetchClasses();
  }, [schoolId, selectedYear]);

  useEffect(() => {
    const fetchIncomeSchools = async () => {
      if (!schoolId) return;
      try {
        const res: ResponseDataType<{ list: IncomeSchoolOption[] }> = await getRequest(
          `/cms/accountant/income-school?schoolId=${schoolId}&page=1&perPage=200`
        );
        if (res?.code === 200) {
          setIncomeSchoolList(res?.info?.list || []);
        }
      } catch (error) {
        console.error('Error fetching income schools:', error);
      }
    };
    fetchIncomeSchools();
  }, [schoolId]);

  const handlePageChange: PaginationProps['onChange'] = (page, nextPageSize) => {
    setCurrentPage(page);
    setPageSize(nextPageSize || 10);
  };

  const handleNotifyClick = async () => {
    if (selectedRowKeys.length === 0) {
      toast.error('Vui lòng chọn ít nhất một hóa đơn để gửi thông báo!');
      return;
    }

    const schoolidNumber = Number(localStorage.getItem('schoolId'));
    const formData = {
      title: 'Thông báo',
      message: 'Thông báo học phí',
      schoolId: schoolidNumber,
      studentInvoiceIds: selectedRowKeys,
    };
    const response: ResponseDataType<NotificationDataType> = await postRequest(
      '/cms/invoice/send-notification-invoice-selected',
      formData,
      'json'
    );

    if (response?.code === 200) {
      toast.success('Gửi thông báo thành công!');
      setSelectedRowKeys([]);
      fetchUnpaidBills();
    } else {
      toast.error('Gửi thông báo thất bại!');
    }
  };

  const confirmSendNotificationToOne = (studentInvoiceId: number, studentName: string) => {
    Modal.confirm({
      title: 'Xác nhận gửi thông báo',
      content: `Bạn có chắc chắn muốn gửi thông báo học phí cho học sinh ${studentName} không?`,
      okText: 'Gửi',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const schoolidNumber = Number(localStorage.getItem('schoolId'));
          const formData = {
            title: 'Thông báo',
            message: 'Thông báo học phí',
            schoolId: schoolidNumber,
            studentInvoiceId: studentInvoiceId,
          };
          const response: ResponseDataType<NotificationDataType> = await postRequest(
            '/cms/invoice/send-notification-invoice-to-one',
            formData,
            'json'
          );
          if (response?.code === 200) {
            toast.success('Gửi thông báo thành công!');
            fetchUnpaidBills();
          } else {
            toast.error(response?.msg || 'Gửi thông báo thất bại!');
          }
        } catch (error) {
          toast.error('Có lỗi xảy ra khi gửi thông báo!');
        }
      },
    });
  };

  const confirmSendNotificationForStudentGroup = (group: StudentBillGroup<unPaidBillDataType>) => {
    if (group.billCount === 1) {
      confirmSendNotificationToOne(group.bills[0].studentInvoiceId, group.studentName);
      return;
    }

    Modal.confirm({
      title: 'Xác nhận gửi thông báo theo học sinh',
      content: `Bạn có chắc chắn muốn gửi thông báo cho cả ${group.billCount} biên lai (Tổng: ${formatCurrency(group.totalAmount)}) của học sinh ${group.studentName} không?`,
      okText: 'Gửi thông báo',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const schoolidNumber = Number(localStorage.getItem('schoolId'));
          const formData = {
            title: 'Thông báo',
            message: 'Thông báo học phí',
            schoolId: schoolidNumber,
            studentInvoiceIds: group.bills.map((b) => b.studentInvoiceId),
          };
          const response: ResponseDataType<NotificationDataType> = await postRequest(
            '/cms/invoice/send-notification-invoice-selected',
            formData,
            'json'
          );
          if (response?.code === 200) {
            toast.success(`Đã gửi thông báo cho ${group.billCount} biên lai của học sinh ${group.studentName}!`);
            fetchUnpaidBills();
          } else {
            toast.error(response?.msg || 'Gửi thông báo thất bại!');
          }
        } catch (error) {
          toast.error('Có lỗi xảy ra khi gửi thông báo!');
        }
      },
    });
  };

  const handleCancelInvoice = (record: unPaidBillDataType) => {
    Modal.confirm({
      title: 'Xác nhận hủy biên lai đã duyệt',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div style={{ marginTop: 8 }}>
          <p style={{ marginBottom: 8 }}>
            Bạn có chắc chắn muốn hủy biên lai <strong>{record.invoidId || record.studentInvoiceId}</strong> của học sinh <strong>{record.studentName}</strong>?
          </p>
          <div style={{ padding: '8px 12px', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4, fontSize: 13, color: '#cf1322' }}>
            ⚠️ <strong>Lưu ý quan trọng:</strong><br />
            • Mã QR thanh toán cũ sẽ lập tức bị vô hiệu hóa.<br />
            • Khoản thu học sinh sẽ được mở khóa để bạn vào Bảng theo dõi lớp điều chỉnh và tạo biên lai mới.
          </div>
        </div>
      ),
      okText: 'Hủy biên lai',
      okType: 'danger',
      cancelText: 'Đóng',
      async onOk() {
        try {
          const res: any = await postRequest('/cms/invoice/cancel-approved-invoice', {
            studentInvoiceId: record.studentInvoiceId,
            reason: 'Hủy từ màn hình Chờ thanh toán để tạo lại',
          });
          if (res?.code === 200) {
            toast.success(res?.msg || 'Đã hủy biên lai thành công!');
            await fetchUnpaidBills();
          } else {
            toast.error(res?.msg || 'Không thể hủy biên lai');
          }
        } catch (error) {
          console.error('Error cancelling approved invoice:', error);
          toast.error('Lỗi khi hủy biên lai');
        }
      },
    });
  };

  const openBillDetail = (studentInvoiceId: number) => {
    setSelectedStudentInvoiceId(studentInvoiceId);
    setHasChanged(false);
    setIsDetailModalOpen(true);
  };

  const masterColumns: TableProps<StudentBillGroup<unPaidBillDataType>>['columns'] = [
    {
      title: (
        <Checkbox
          checked={isPageAllChecked}
          indeterminate={isPageIndeterminate}
          disabled={allPageInvoiceIds.length === 0}
          onChange={handleToggleSelectAllPage}
        />
      ),
      key: 'selection',
      width: 48,
      align: 'center',
      className: 'cell-center',
      render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
        const state = getGroupSelectionState(group.bills, selectedRowKeys);
        return (
          <Checkbox
            checked={state.checked}
            indeterminate={state.indeterminate}
            disabled={state.disabled}
            onChange={() =>
              setSelectedRowKeys((prev) => toggleGroupSelectionKeys(group.bills, prev))
            }
          />
        );
      },
    },
    {
      title: 'Mã biên lai',
      key: 'invoidId',
      align: 'center',
      className: 'cell-center',
      width: 140,
      ellipsis: {
        showTitle: false,
      },
      render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
        if (group.billCount === 1) {
          const singleBill = group.bills[0];
          return <EllipsisTooltip text={singleBill.invoidId || singleBill.billId || ''} />;
        }
        const billCodes = group.bills
          .map((b) => b.invoidId || b.billId)
          .filter(Boolean)
          .join(', ');
        return (
          <Tooltip title={billCodes ? `Mã biên lai: ${billCodes}` : undefined}>
            <span>{group.billCount} biên lai</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'CCCD',
      dataIndex: 'studentCode',
      key: 'studentCode',
      align: 'center',
      className: 'cell-center',
      width: 135,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => <EllipsisTooltip text={text} />,
    },
    {
      title: 'Tên học sinh',
      dataIndex: 'studentName',
      key: 'studentName',
      align: 'left',
      width: 190,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => <EllipsisTooltip text={text} />,
    },
    {
      title: 'Lớp học',
      dataIndex: 'className',
      key: 'className',
      render: (text: string) => <EllipsisTooltip text={text || ''} />,
      align: 'center',
      className: 'cell-center',
      width: 100,
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: 'Số tiền',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      width: 140,
      ellipsis: {
        showTitle: false,
      },
      render: (totalAmount: number) => (
        <span className="accounting-amount">
          {formatCurrency(totalAmount)}
        </span>
      ),
    },
    {
      title: 'Nội dung',
      dataIndex: 'contentSummary',
      key: 'contentSummary',
      align: 'left',
      ellipsis: {
        showTitle: false,
      },
      render: (contentSummary: string) => <EllipsisTooltip text={contentSummary} />,
    },
    {
      title: 'Số lần gửi thông báo',
      key: 'notificationCount',
      align: 'center',
      className: 'cell-center',
      width: 135,
      render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
        const count =
          group.billCount === 1
            ? group.bills[0].notificationCount || 0
            : group.totalNotificationCount;
        const tooltipText =
          group.billCount === 1
            ? 'Gửi thông báo'
            : `Gửi thông báo cho cả ${group.billCount} biên lai của học sinh`;
        return (
          <Tooltip title={tooltipText}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <MailOutlined
                style={{ fontSize: '32px', color: '#1890ff', cursor: 'pointer' }}
                onClick={() => confirmSendNotificationForStudentGroup(group)}
              />
              <Badge
                count={count}
                size="small"
                showZero
                style={{ position: 'absolute', bottom: -8, right: -6 }}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Hành động',
      key: 'action',
      width: 145,
      align: 'center',
      className: 'cell-center',
      render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
        if (group.billCount === 1) {
          const singleBill = group.bills[0];
          return (
            <Space size="middle">
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() => openBillDetail(singleBill.studentInvoiceId)}
              >
                Chi tiết
              </Button>
              <Button
                type="link"
                danger
                style={{ padding: 0 }}
                onClick={() => handleCancelInvoice(singleBill)}
              >
                Hủy
              </Button>
            </Space>
          );
        }

        return <span style={{ color: '#bfbfbf' }}>—</span>;
      },
    },
  ];

  const renderExpandedBills = (group: StudentBillGroup<unPaidBillDataType>) => {
    const subColumns: TableProps<unPaidBillDataType>['columns'] = [
      {
        title: 'Mã biên lai',
        dataIndex: 'invoidId',
        key: 'invoidId',
        width: 150,
        align: 'center',
        render: (text: string, record: unPaidBillDataType) => (
          <EllipsisTooltip text={text || record.billId || ''} />
        ),
      },
      {
        title: 'Nội dung khoản thu',
        dataIndex: 'content',
        key: 'content',
        align: 'left',
        render: (content: string) => <EllipsisTooltip text={content} />,
      },
      {
        title: 'Số tiền',
        dataIndex: 'cost',
        key: 'cost',
        width: 145,
        align: 'right',
        render: (cost: number) => (
          <span className="accounting-amount">{formatCurrency(cost)}</span>
        ),
      },
      {
        title: 'Số lần gửi thông báo',
        dataIndex: 'notificationCount',
        key: 'notificationCount',
        width: 140,
        align: 'center',
        render: (val: number | undefined, record: unPaidBillDataType) => (
          <Tooltip title="Gửi thông báo khoản thu này">
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <MailOutlined
                style={{ fontSize: '26px', color: '#1890ff', cursor: 'pointer' }}
                onClick={() => confirmSendNotificationToOne(record.studentInvoiceId, record.studentName)}
              />
              <Badge
                count={val || 0}
                size="small"
                showZero
                style={{ position: 'absolute', bottom: -6, right: -6 }}
              />
            </div>
          </Tooltip>
        ),
      },
      {
        title: 'Hành động',
        dataIndex: 'studentInvoiceId',
        key: 'action',
        width: 145,
        align: 'center',
        render: (studentInvoiceId: number, record: unPaidBillDataType) => (
          <Space size="middle">
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => openBillDetail(studentInvoiceId)}
            >
              Chi tiết
            </Button>
            <Button
              type="link"
              danger
              style={{ padding: 0 }}
              onClick={() => handleCancelInvoice(record)}
            >
              Hủy
            </Button>
          </Space>
        ),
      },
    ];

    const groupSelectedKeys = group.bills
      .filter((b) => selectedRowKeys.includes(b.studentInvoiceId))
      .map((b) => b.studentInvoiceId);

    const handleSubSelectionChange = (newSubKeys: React.Key[]) => {
      const groupAllIds = new Set<React.Key>(group.bills.map((b) => b.studentInvoiceId));
      setSelectedRowKeys((prev) => {
        const otherKeys = prev.filter((k) => !groupAllIds.has(k));
        return [...otherKeys, ...newSubKeys];
      });
    };

    return (
      <div className="student-subtable-container">
        <Table
          size="small"
          bordered
          pagination={false}
          rowKey="studentInvoiceId"
          columns={subColumns}
          dataSource={group.bills}
          rowSelection={{
            selectedRowKeys: groupSelectedKeys,
            onChange: handleSubSelectionChange,
          }}
        />
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Select
            placeholder="Năm"
            value={selectedYear}
            onChange={(value) => {
              setSelectedYear(value);
              setSelectedClass(undefined);
              setCurrentPage(1);
            }}
            style={{ width: 120 }}
          >
            {years.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Tháng"
            value={selectedMonth}
            onChange={(value) => {
              setSelectedMonth(value);
              setCurrentPage(1);
            }}
            style={{ width: 120 }}
          >
            {MONTHS.map((month) => (
              <Option key={month.value} value={month.value}>
                {month.label}
              </Option>
            ))}
          </Select>
          {selectedMonth === getStoredWorkMonth().month && selectedYear === getStoredWorkMonth().year ? (
            <Tag color="blue" style={{ height: 32, display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500, margin: 0 }}>
              <CalendarOutlined /> Kỳ làm việc
            </Tag>
          ) : (
            <Tooltip title="Đang xem kỳ khác. Bấm để quay về Kỳ làm việc trên thanh Header">
              <Button
                size="middle"
                icon={<SyncOutlined />}
                onClick={() => {
                  const wm = getStoredWorkMonth();
                  setSelectedMonth(wm.month);
                  setSelectedYear(wm.year);
                  setCurrentPage(1);
                }}
              >
                Về kỳ T{getStoredWorkMonth().month}/{getStoredWorkMonth().year}
              </Button>
            </Tooltip>
          )}
          <ClassLevelSelect
            value={selectedClassLevel}
            onChange={(val) => {
              setSelectedClassLevel(val);
              setSelectedClass(undefined);
              setCurrentPage(1);
            }}
            placeholder="Tất cả khối"
            style={{ width: 130 }}
          />
          <Select
            showSearch
            allowClear
            placeholder="Lớp học"
            value={selectedClass}
            onChange={(value) => {
              setSelectedClass(value);
              setCurrentPage(1);
            }}
            style={{ width: 150 }}
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {classList
              .filter((cls) => !selectedClassLevel || String(cls.classLevel).trim() === selectedClassLevel.trim())
              .map((cls) => (
                <Option key={cls.classYearId} value={cls.classYearId}>
                  {cls.className}
                </Option>
              ))}
          </Select>
          <Select
            showSearch
            allowClear
            placeholder="Tất cả nguồn thu"
            value={selectedIncomeSchoolId}
            onChange={(value) => {
              setSelectedIncomeSchoolId(value);
              setCurrentPage(1);
            }}
            style={{ width: 180 }}
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {incomeSchoolList.map((src) => (
              <Option key={src.id} value={src.id}>
                {src.name}
              </Option>
            ))}
          </Select>
          <Input
            placeholder="Tìm tên học sinh"
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            allowClear
            value={searchStudentName}
            onChange={(e) => setSearchStudentName(e.target.value)}
            onPressEnter={handlePressEnter}
            style={{ width: 180 }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {multiBillGroupKeys.length > 0 && (
            <Button
              icon={isAllExpanded ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
              onClick={handleToggleExpandAll}
            >
              {isAllExpanded ? 'Thu gọn chi tiết' : `Mở rộng chi tiết (${multiBillGroupKeys.length} HS)`}
            </Button>
          )}
          <Button type="primary" onClick={handleNotifyClick}>
            Gửi thông báo
          </Button>
        </div>
      </div>

      {selectedRowKeys.length > 0 && (
        <Alert
          message={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Typography.Text style={{ fontSize: 14 }}>
                  Đã chọn:{' '}
                  <Typography.Text strong style={{ color: '#1890ff' }}>
                    {selectedRowKeys.length} biên lai
                  </Typography.Text>
                  <span style={{ color: '#6b7280', marginLeft: 6 }}>
                    ({selectedStudentCount} học sinh)
                  </span>
                </Typography.Text>
                <div style={{ width: 1, height: 16, backgroundColor: '#d9d9d9' }} />
                <Typography.Text style={{ fontSize: 14 }}>
                  Tổng phải thu đã chọn:{' '}
                  <Typography.Text strong style={{ fontSize: 16, color: '#cf1322' }}>
                    {formatCurrency(totalSelectedAmount)}
                  </Typography.Text>
                </Typography.Text>
              </div>
              <Button size="small" type="link" onClick={() => setSelectedRowKeys([])}>
                Bỏ chọn tất cả
              </Button>
            </div>
          }
          type="info"
          showIcon
          style={{
            marginBottom: 12,
            padding: '6px 14px',
            borderRadius: 8,
            border: '1px solid #91d5ff',
            backgroundColor: '#e6f7ff',
          }}
        />
      )}

      <Table
        tableLayout="fixed"
        loading={loading}
        columns={masterColumns}
        dataSource={studentGroups}
        rowKey="groupKey"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
          expandedRowRender: renderExpandedBills,
          rowExpandable: (group) => group.billCount > 1,
        }}
        summary={() => {
          if (studentGroups.length === 0) return null;
          const pageTotal = studentGroups.reduce((sum, g) => sum + g.totalAmount, 0);
          return (
            <Table.Summary.Row className="bill-table-summary-row">
              <Table.Summary.Cell index={0} colSpan={6}>
                Tổng trang hiện tại: {studentGroups.length} học sinh ({unPaidBillList.length} biên lai)
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <span className="accounting-amount" style={{ fontWeight: 600 }}>
                  {formatCurrency(pageTotal)}
                </span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} colSpan={3} />
            </Table.Summary.Row>
          );
        }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          pageSizeOptions: ['10', '50', '100', '200', '500'],
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} học sinh (${unPaidBillList.length} biên lai trên trang)`,
          onChange: handlePageChange,
        }}
      />

      <Modal
        title="Chi tiết biên lai"
        open={isDetailModalOpen}
        onCancel={() => {
          setIsDetailModalOpen(false);
          setSelectedStudentInvoiceId(undefined);
          if (hasChanged) {
            fetchUnpaidBills();
            setHasChanged(false);
          }
        }}
        footer={null}
        width={1500}
        destroyOnClose
      >
        <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {selectedStudentInvoiceId && (
            <BillInfo
              studentInvoiceId={selectedStudentInvoiceId}
              onSuccess={() => setHasChanged(true)}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};
